import React, { useEffect, useState } from "react";
import { Image } from "react-bootstrap";
import {
  StreakConfigList,
  StreakStatus,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";

import "./StreaksSection.scss";
import ImageHourglass from "../../../../images/hourglass.svg";
import GhostButton from "../../GhostButton/GhostButton";
import StreakExpiringModal from "../../modals/StreakExpiringModal/StreakExpiringModal";
import StreakModal from "../../modals/StreakModal/StreakModal";
import StreakDays from "../../StreakDays/StreakDays";
import {
  handleGetAvailableStreaks,
  handleGetStreakStatus,
} from "../../../../api-clients/streaksClient";
import {
  TimeUnit,
  getEndOfDay,
  getTimeDifferenceToNow,
} from "../../../../utils/helpers";
import Skeleton from "../../../Skeleton/Skeleton";
import SkeletonList from "../../../SkeletonList/SkeletonList";
import { ThemeVariant } from "../../../../utils/constants/generic-types";

const SHOW_TIME_LEFT_THRESHOLD = 6;
const SKELETON_SHIMMER_STYLE = { background: "#1b0c3dff" };
const SKELETON_BG_STYLE = { background: "rgba(51, 23, 120, 0.1)" };

function LoadingSkeleton() {
  return (
    <section className="streaks-section streaks-section-loading px-3">
      <Skeleton
        className="streak-heading-skeleton"
        width={116}
        height={18}
        borderRadius={4}
        shimmerStyle={SKELETON_SHIMMER_STYLE}
        style={SKELETON_BG_STYLE}
        theme={ThemeVariant.DARK}
      />
      <div className="position-relative streak-card-skeleton">
        <div className="days-container w-100 streak-days-skeleton">
          <SkeletonList noOfItems={7}>
            <div className="streak-day-skeleton-item">
              <Skeleton
                width={24}
                height={24}
                variant="rounded"
                shimmerStyle={SKELETON_SHIMMER_STYLE}
                style={SKELETON_BG_STYLE}
                theme={ThemeVariant.DARK}
              />
              <Skeleton
                width={16}
                height={8}
                borderRadius={4}
                shimmerStyle={SKELETON_SHIMMER_STYLE}
                style={SKELETON_BG_STYLE}
                theme={ThemeVariant.DARK}
              />
            </div>
          </SkeletonList>
        </div>
        <div className="copy w-100 streak-copy-skeleton">
          <Skeleton
            width="68%"
            height={14}
            borderRadius={4}
            shimmerStyle={SKELETON_SHIMMER_STYLE}
            style={SKELETON_BG_STYLE}
            theme={ThemeVariant.DARK}
          />
        </div>
        <div className="streak-time-left-skeleton">
          <Skeleton
            width={84}
            height={28}
            borderRadius={100}
            shimmerStyle={SKELETON_SHIMMER_STYLE}
            style={SKELETON_BG_STYLE}
            theme={ThemeVariant.DARK}
          />
        </div>
      </div>
    </section>
  );
}

function StreaksSection() {
  const { t } = useTranslation("dashboard");
  const [isExpiringModalOpen, setIsExpiringModalOpen] = useState(false);
  const [isStreakModalOpen, setIsStreakModalOpen] = useState(false);
  const [streakStatus, setStreakStatus] = useState<StreakStatus>();
  const [streakConfig, setStreakConfig] = useState<StreakConfigList>();
  const [isLoading, setIsLoading] = useState(true);

  const todaysReward =
    streakConfig &&
    streakStatus &&
    streakConfig.records.find(
      (record) => record.milestone === streakStatus.streakCount + 1
    );

  const timeLeft = getTimeDifferenceToNow(getEndOfDay());
  const canShowTimeLeft =
    ((timeLeft?.unit === TimeUnit.HOUR &&
      timeLeft.difference <= SHOW_TIME_LEFT_THRESHOLD) ||
      timeLeft?.unit === TimeUnit.MINUTE) &&
    !!streakStatus?.streakCount &&
    !streakStatus?.todayCompleted;

  let timeLeftCopy = "";
  if (timeLeft) {
    timeLeftCopy = `${timeLeft.difference} ${timeLeft.unit}${
      timeLeft.difference > 1 ? "s" : ""
    }`;
  }

  useEffect(() => {
    const getStreakStatus = async () => {
      const [streak, streakConfigList] = await Promise.all([
        handleGetStreakStatus(),
        handleGetAvailableStreaks(),
      ]);
      setStreakConfig(streakConfigList);
      setStreakStatus(streak);
      setIsLoading(false);
    };

    getStreakStatus();
  }, []);

  if (isLoading) return <LoadingSkeleton />;

  if (!streakStatus || streakStatus.todayCompleted) return null;

  return (
    <section className="streaks-section px-3">
      <div className="heading-container d-flex justify-content-between align-items-center">
        <h2 className="heading">{t("daily_streaks")}</h2>
      </div>

      <div className="position-relative">
        <GhostButton
          aria-label="view streak info"
          className="w-100 p-0 flex-column"
          onClick={() => setIsStreakModalOpen(true)}
        >
          <div className="days-container w-100">
            <StreakDays status={streakStatus} />
          </div>
          {todaysReward && (
            <div className="copy w-100">
              {streakStatus.streakCount > 0
                ? t("play_a_game_to_extend_your_streak")
                : t("play_a_game_to_start_a_streak")}
            </div>
          )}
        </GhostButton>
        {canShowTimeLeft && (
          <GhostButton
            className="time-left"
            onClick={() => setIsExpiringModalOpen(true)}
          >
            <Image src={ImageHourglass} alt="hourglass" className="hourglass" />
            <span>{timeLeftCopy}</span>
          </GhostButton>
        )}
      </div>

      <StreakExpiringModal
        show={isExpiringModalOpen}
        onHide={() => setIsExpiringModalOpen(false)}
        time={timeLeftCopy}
      />
      {isStreakModalOpen && streakConfig && (
        <StreakModal
          show={isStreakModalOpen}
          onHide={() => setIsStreakModalOpen(false)}
          status={streakStatus}
          config={streakConfig}
          canShowTimeLeft={canShowTimeLeft}
          timeLeftCopy={timeLeftCopy}
        />
      )}
    </section>
  );
}

export default StreaksSection;
